import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PRESETS } from '@ic/engine';
import { HeroBoard } from '../components/HeroBoard';
import { HoodAd } from '../components/HoodAd';
import { PiecePlayground } from '../components/PiecePlayground';
import { OnlineNow } from '../components/OnlineNow';
import { Icon } from '../components/Icons';
import { auth } from '../lib/auth';
import { publishedModes } from '../lib/modes';
import { activeRuleset, useStore } from '../lib/store';
import { navigateMorph } from '../lib/transition';
import { prefs, type Mode } from './Play';

function ActionRow({ to, onPick, icon, title, desc, primary }: { to: string; onPick: (el: HTMLElement) => void; icon: ReactNode; title: string; desc: string; primary?: boolean }) {
  return (
    <Link to={to} onClick={(e) => { e.preventDefault(); onPick(e.currentTarget); }} className={`action-row ${primary ? 'primary' : ''}`}>
      <span className="action-icon">{icon}</span>
      <span className="action-text"><b>{title}</b><span>{desc}</span></span>
      <Icon name="arrow" size={18} className="action-arrow" />
    </Link>
  );
}

const QUICK_PRESETS = ['illegal', 'standard', 'bomb-squad', 'mini'];

export function Home() {
  const nav = useNavigate();
  const { user, admin } = useStore(auth);
  const modes = useStore(publishedModes);
  const rs = useStore(activeRuleset);
  const [quickId, setQuickId] = useState('illegal');
  const pickMode = (mode: Mode) => (el: HTMLElement) => { prefs.set({ ...prefs.get(), mode }); navigateMorph(nav, '/play/setup', el, 'mode'); };
  const pickPage = (to: string) => (el: HTMLElement) => navigateMorph(nav, to, el, 'page');
  const selectedPreset = PRESETS.find((p) => p.id === quickId) ?? PRESETS[0];
  const startQuick = (el: HTMLElement) => {
    activeRuleset.set(JSON.parse(JSON.stringify(selectedPreset)));
    navigateMorph(nav, '/play/bot?level=2&color=w&tc=0+0', el, 'board');
  };
  return (
    <div className="page home">
      <section className="home-grid">
        <div className="home-board">
          <HeroBoard presetId={quickId} />
        </div>
        <div className="home-actions">
          <span className="home-kicker">Choose your chaos</span>
          <h1>{user && !user.guest ? `Welcome back, ${user.name}` : 'Chess, with every rule unlocked.'}</h1>
          <OnlineNow />
          <p className="lead">Pick a board and watch it come alive. Play classic chess, or break it with Dragons, Bombs, Miners, Frogs, Gardeners, Cooks, Cockroaches and the Illegal Elephant.</p>
          <section className="quickstart" aria-labelledby="quickstart-title">
            <div className="quickstart-head">
              <span className="quickstart-kicker">Live preview · no sign-up</span>
              <h2 id="quickstart-title">Pick a board, then play it</h2>
            </div>
            <div className="quickstart-tabs" role="tablist" aria-label="Quick-start boards">
              {QUICK_PRESETS.map((id) => {
                const preset = PRESETS.find((p) => p.id === id)!;
                return <button key={id} type="button" role="tab" aria-selected={quickId === id} className={quickId === id ? 'on' : ''} onClick={() => setQuickId(id)}>{preset.name}</button>;
              })}
            </div>
            <div className="quickstart-choice">
              <div><b>{selectedPreset.name}</b><span>{selectedPreset.description}</span></div>
              <button type="button" className="btn primary" onClick={(e) => startQuick(e.currentTarget)}>Play this board <Icon name="arrow" size={16} /></button>
            </div>
          </section>
          <div className="action-list">
            <ActionRow to="/play/setup" onPick={pickMode('online')} primary icon={<Icon name="globe" size={24} />} title="Play online" desc="Get matched with a player using the same rules" />
            <ActionRow to="/play/setup" onPick={pickMode('bot')} icon={<Icon name="cpu" size={24} />} title="Play the computer" desc="Eight strength levels, also offline" />
            <ActionRow to="/play/setup" onPick={pickMode('friend')} icon={<Icon name="link" size={24} />} title="Play a friend" desc="Send an invite link" />
            <ActionRow to="/play/setup" onPick={pickMode('local')} icon={<Icon name="users" size={24} />} title="Pass & play" desc="Two players on one device" />
            <ActionRow to="/play/setup" onPick={pickMode('four')} icon={<span className="four-dots"><i className="w" /><i className="g" /><i className="b" /><i className="y" /></span>} title="4 players" desc="Free for all with Green and Yellow" />
            <ActionRow to="/puzzles" onPick={pickPage('/puzzles')} icon={<Icon name="target" size={24} />} title="Solve puzzles" desc="Find the winning move" />
            {admin && <ActionRow to="/editor" onPick={pickPage('/editor')} icon={<Icon name="dig" size={24} />} title="Rule Editor" desc="Change the board, pieces and rules" />}
          </div>
          <p className="muted small">Next game uses <b>{rs.name}</b> · <Link to="/play/setup">change</Link></p>
        </div>
      </section>
      <PiecePlayground />
      <HoodAd />
      <section>
        <h2>Variants</h2>
        <div className="preset-grid">
          {[...PRESETS, ...modes].map((p) => (
            <button key={p.id} className="preset-card" onClick={(e) => { activeRuleset.set(JSON.parse(JSON.stringify(p))); navigateMorph(nav, '/play/setup', e.currentTarget, 'preset'); }}>
              <b>{p.name}</b>
              <span className="muted small">{p.description}</span>
              <span className="tag">{p.board.width}×{p.board.height}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
